import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { ActivityAsyncAdapter } from './activity.adapter';

import { IModel } from 'app/model/base/annotation';
import { Metamodel } from 'app/model/base/model';
import {
    WebActivity,
    HttpAuthenticationType,
    WebActivitySPNAuthenticationType,
    WebActivitySettingsType
} from 'app/model/pipeline/web.activity';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import _ from 'lodash';
import { ReferenceAdapter } from 'app/model/adapters/reference.adapter';
import { convertParametersToModel, fillParameterValuesForResource } from 'app/shared/utils/parameter-converter.util';
import { LinkedService } from 'app/model/linkedservice/linkedservice';
import { Dataset } from 'app/model/dataset/dataset';
import { ReferenceObjectParameters } from 'app/model/pipeline/reference-object-parameters';
import { ParameterValue } from 'app/model/base/parameter-value';
import { AdapterContext, ConnectionKind, Credential, Named, ModelConstants } from 'shell-models-lib';
import { Owned } from 'app/model/base/named';
import { linkedServiceReferenceWithoutParameterizationRemainer } from '../reference-remainer';
import { toParameterClass, toParameterObject } from 'app/model/adapters/linkedservices/parameter-config.util';
import { IntegrationRuntime } from '../../integrationRuntime/integrationRuntime';
import { passwordOrAKVtoSecretBase, secretBaseToPasswordOrAKV } from '../linkedservices/azure-key-vault.util';
import { isExpressionObject } from 'app/shared/utils/expression.util';
import { GlobalVariables } from 'app/services/globals';
import { ActivityConnectionContext, IWebActivityContext } from 'app/interfaces/copilot.pipeline.action.types';
import { getServiceInstance } from 'app/multi-contexts/modules/root/root.application';
import { findLinkedServiceFromContext } from 'app/shared/utils/update-pipeline-skill.util';
import { type ModelService } from 'app/model/services/model.service';
import { IFrameService } from 'app/services/iframe.service';
import { A365WorkspaceCacheService } from 'app/services/metadata-client/A365workspace-cache.service';
import { RestServiceLinkedService } from 'app/model/linkedservice/rest-service.linkedservice';
import { isUrlValid, getFabricLsForParameterizedConnections } from '../utils';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(WebActivity),
    asynchronous: true,
    adapts: {
        name: '*',
        description: '*',
        type: '*',
        linkedServiceName: '*',
        policy: '*',
        dependsOn: '*',
        userProperties: '*',
        typeProperties: {
            connectVia: '*',
            method: '*',
            url: '*',
            relativeUrl: '*',
            resource: '*',
            headers: '*',
            body: '*',
            httpRequestTimeout: '*',
            turnOffAsync: '*',
            disableFollowAuthOnRedirect: '*',
            pollUrlPattern: '*',
            pollStatusKey: '*',
            pollSuccessStatusValues: '*',
            pollFailedStatusValues: '*',
            authentication: (context: AdapterContext, value: Model.WebActivityAuthentication) => {
                if (
                    value &&
                    value.type &&
                    (value.type.toUpperCase() === 'NONE' ||
                        value.type.toUpperCase() === 'BASIC' ||
                        value.type.toUpperCase() === 'MSI' ||
                        value.type.toUpperCase() === 'CLIENTCERTIFICATE' ||
                        value.type.toUpperCase() === 'SERVICEPRINCIPAL' ||
                        value.type.toUpperCase() === 'USERAUTH' ||
                        value.type.toUpperCase() === 'USERASSIGNEDMANAGEDIDENTITY')
                ) {
                    return '*';
                }
                // Web activity UI only support None, Basic, ClientCertificate, ServicePrincipal auth.
                // Returning anything other than * or ? will add it to raw JSON.
                return undefined;
            },
            datasets: '*',
            linkedServices: linkedServiceReferenceWithoutParameterizationRemainer,
            userTenant: '*',
            useFileContentForRequestBody: '*',
            linkedServiceForRequestBody: linkedServiceReferenceWithoutParameterizationRemainer,
            filePathForRequestBody: '*',
            responseJsonPath: '*'
        },
        externalReferences: '*'
    }
})
export class WebActivityAdapter extends ActivityAsyncAdapter<WebActivity, Model.WebActivity, IWebActivityContext> {
    public isTrident: boolean = __IS_FABRIC_DPE__;
    public readonly enableRedirectDisableFeature = GlobalVariables.enableRedirectDisable;

    public model(): IModel {
        return Metamodel.modelFor(WebActivity);
    }

    public override async toFabricCopilotContext(activity: WebActivity): Promise<IWebActivityContext> {
        if (activity) {
            const activityContext: IWebActivityContext = await super.toFabricCopilotContext(activity);
            if (activity.webLinkedService) {
                const lsAdapter = this.context.asyncAdapterFor<
                    LinkedService,
                    Model.LinkedServiceResource,
                    ActivityConnectionContext
                >(Metamodel.modelOf(activity.webLinkedService));
                activityContext.Connection = await lsAdapter?.toFabricCopilotContext(activity.webLinkedService);
                activityContext.RelativeUrl = ValueAdapter.toResource(activity, WebActivity.RELATIVEURL, true);
                activityContext.Method = activity.method;
                activityContext.Body = JSON.stringify(ValueAdapter.toResource(activity, WebActivity.BODY, true));
                activityContext.Headers = toParameterObject(activity.headers, false, undefined, true);
                activityContext.Timeout = ValueAdapter.toResource(activity, WebActivity.HTTPREQUESTTIMEOUT, true);
            }
            return activityContext;
        }
        return undefined;
    }

    public override async fromFabricCopilotContext(
        context: IWebActivityContext,
        model?: WebActivity
    ): Promise<WebActivity> {
        if (context) {
            model = await super.fromFabricCopilotContext(context, model);
            if (context.Connection) {
                const modelService = await getServiceInstance<ModelService>(this.context.contextData, 'ModelService');
                const iframeService = await getServiceInstance<IFrameService>(
                    this.context.contextData,
                    'IFrameService'
                );
                model.webLinkedService = (
                    await findLinkedServiceFromContext<LinkedService>(context.Connection, {
                        modelService,
                        iframeService
                    })
                ).linkedService;
                if (model.webLinkedService) {
                    const lsAdapter = Metamodel.isModel(context.Connection.Type)
                        ? this.context.asyncAdapterFor<
                              LinkedService,
                              Model.LinkedServiceResource,
                              ActivityConnectionContext
                          >(Metamodel.modelByName(context.Connection.Type))
                        : undefined;
                    await lsAdapter?.fromFabricCopilotContext(context.Connection, model.webLinkedService);
                    ValueAdapter.toModel(model, WebActivity.RELATIVEURL, context.RelativeUrl);
                    try {
                        ValueAdapter.toModel(model, WebActivity.BODY, JSON.parse(context.Body));
                    } catch (e) {}
                    ValueAdapter.toModel(model, WebActivity.HTTPREQUESTTIMEOUT, context.Timeout);
                    if (context.Method) {
                        model.method = context.Method;
                    }
                    if (context.Headers) {
                        model.headers = toParameterClass(context.Headers, model);
                    }
                }
            }
        }
        return model;
    }

    public async postToModel(activity: WebActivity): Promise<void> {
        await super.postToModel(activity);
        activity.showDEPWarning = false;
        if (__IS_A365__ && activity.connectVia == null && !GlobalVariables.isCmdApiApp) {
            const a365WorkspaceCacheService = await getServiceInstance<A365WorkspaceCacheService>(
                this.context.contextData,
                'A365WorkspaceCacheService'
            );
            activity.showDEPWarning = a365WorkspaceCacheService.isDEPEnabled;
        }
    }

    protected async _toResource(activity: WebActivity): Promise<Model.WebActivity> {
        const modelActivity: Model.WebActivity = super._getCommonActivityProperties(
            activity,
            'WebActivity'
        ) as Model.WebActivity;

        const headers = toParameterObject(activity.headers);
        modelActivity.typeProperties = {
            method: activity.method || undefined,
            headers: headers ? headers : {},
            linkedServices:
                activity.linkedServices.length > 0
                    ? activity.linkedServices
                          .filter((linkedService) => linkedService !== undefined)
                          .map((linkedService) => this._toLinkedServiceReferenceModel(linkedService))
                    : undefined,
            datasets: activity.datasets.length > 0 ? this.getDatasetsReferences(activity) : undefined,
            disableCertValidation: ValueAdapter.toResource(activity, WebActivity.DISABLECERTVALIDATION),
            httpRequestTimeout: ValueAdapter.toResource(activity, WebActivity.HTTPREQUESTTIMEOUT),
            turnOffAsync: ValueAdapter.toResource(activity, WebActivity.TURNOFFASYNC),
            pollUrlPattern: ValueAdapter.toResource(activity, WebActivity.GETSTATUS_URL_PATTERN),
            pollStatusKey: ValueAdapter.toResource(activity, WebActivity.GETSTATUS_STATE_KEY),
            pollSuccessStatusValues: ValueAdapter.toResource(activity, WebActivity.GETSTATUS_SUCCESS_RESPONSE_VALUES),
            pollFailedStatusValues: ValueAdapter.toResource(activity, WebActivity.GETSTATUS_FAILURE_RESPONSE_VALUES),
            useFileContentForRequestBody: ValueAdapter.toResource(activity, WebActivity.USEFILECONTENTFORREQUESTBODY),
            responseJsonPath: ValueAdapter.toResource(activity, WebActivity.RESPONSEJSONPATH)
        };

        if (this.enableRedirectDisableFeature === true) {
            modelActivity.typeProperties.disableFollowAuthOnRedirect = ValueAdapter.toResource(
                activity,
                WebActivity.DISABLEFOLLOWAUTHONREDIRECT
            );
        }

        let webLinkedService: RestServiceLinkedService = activity.webLinkedService as RestServiceLinkedService;
        if (this.context.adapterConfig.convertToFabric) {
            const linkedServiceName = ValueAdapter.isTemplated(activity, WebActivity.URL) 
                ? `Url: ${ValueAdapter.toResource(activity, WebActivity.URL, true)}`
                : activity.url && isUrlValid(activity.url)
                    ? `Url: ${new URL(activity.url).hostname}`
                    : 'Url: default';

            webLinkedService = new RestServiceLinkedService(linkedServiceName);
        }
        
        if (
            this.context.adapterConfig.convertToFabric ||
            activity.activitySettings === WebActivitySettingsType.CONNECTION ||
            activity.activitySettings === WebActivitySettingsType.LINKED_SERVICE
        ) {
            if (webLinkedService) {
                modelActivity.linkedServiceName = this._toLinkedServiceReferenceModel(
                    webLinkedService,
                    activity.linkedServiceParameters
                );
                if (
                    this.context.adapterConfig.convertToFabric &&
                    activity.linkedServiceType != null &&
                    Named.OBJECT_ID.templateValue(activity.webLinkedService)
                ) {
                    modelActivity.linkedServiceName.referenceType = activity.linkedServiceType ?? ConnectionKind.WEB;
                }
            }
            modelActivity.typeProperties.relativeUrl = ValueAdapter.toResource(activity, WebActivity.RELATIVEURL);
        } else {
            modelActivity.typeProperties.url = ValueAdapter.toResource(activity, WebActivity.URL);
            modelActivity.typeProperties.connectVia = activity.connectVia
                ? this._toIntegrationRuntimeModel(activity.connectVia)
                : undefined;
        }

        if (modelActivity.typeProperties.useFileContentForRequestBody) {
            modelActivity.typeProperties.linkedServiceForRequestBody = this._toLinkedServiceReferenceModel(
                activity.linkedServiceForRequestBody
            );
            modelActivity.typeProperties.filePathForRequestBody = ValueAdapter.toResource(
                activity,
                WebActivity.FILEPATHFORREQUESTBODY
            );
        } else {
            modelActivity.typeProperties.body = ValueAdapter.toResource(activity, WebActivity.BODY);
        }

        if (!this.context.adapterConfig.convertToFabric &&
            activity.authentication &&
            activity.authentication.type !== 'None' &&
            activity.activitySettings === WebActivitySettingsType.INLINE
        ) {
            // irrespective of auth type, if auth ref is credential, populate cred and resource instead of type
            if (
                activity.authentication.refType === 'Credential' ||
                activity.authentication.type === 'UserAssignedManagedIdentity' ||
                activity.authentication.type === 'UserAuth'
            ) {
                modelActivity.typeProperties.authentication = {};
                modelActivity.typeProperties.authentication.resource = ValueAdapter.toResource(
                    activity.authentication,
                    HttpAuthenticationType.RESOURCE
                );
                if (!(__IS_FABRIC_DPE__ || this.context.adapterConfig.convertToFabric)) {
                    if (!!activity.authentication.credential) {
                        modelActivity.typeProperties.authentication.credential = ReferenceAdapter.toResource(
                            activity.authentication.credential,
                            this.context
                        ) as Model.CredentialReference;
                    }
                    if (activity.authentication.type === 'UserAssignedManagedIdentity') {
                        modelActivity.typeProperties.authentication.type = activity.authentication.type;
                    }
                } else {
                    modelActivity.typeProperties.authentication.type = activity.authentication.type;
                }
            } else {
                modelActivity.typeProperties.authentication = {
                    type: activity.authentication.type
                };
            }

            // inline options
            if (activity.authentication.type === 'Basic') {
                modelActivity.typeProperties.authentication.username = ValueAdapter.toResource(
                    activity.authentication,
                    HttpAuthenticationType.USERNAME
                );
            } else if (activity.authentication.type === 'ClientCertificate' && activity.authentication.pfx) {
                modelActivity.typeProperties.authentication.pfx = passwordOrAKVtoSecretBase(
                    activity.authentication.pfx,
                    this.context
                );
            } else if (activity.authentication.type === 'MSI') {
                modelActivity.typeProperties.authentication.resource = ValueAdapter.toResource(
                    activity.authentication,
                    HttpAuthenticationType.RESOURCE
                );
            } else if (activity.authentication.type === 'ServicePrincipal') {
                if (
                    activity.authentication.pfx &&
                    activity.authentication.spnAuthenticationFormat ===
                        WebActivitySPNAuthenticationType.WEB_SERVICEPRINCIPAL_CERTIFICATE
                ) {
                    modelActivity.typeProperties.authentication.pfx = passwordOrAKVtoSecretBase(
                        activity.authentication.pfx,
                        this.context
                    );
                }

                modelActivity.typeProperties.authentication.userTenant = ValueAdapter.toResource(
                    activity.authentication,
                    HttpAuthenticationType.USERTENANT
                );
                modelActivity.typeProperties.authentication.username = ValueAdapter.toResource(
                    activity.authentication,
                    HttpAuthenticationType.USERNAME
                );
                modelActivity.typeProperties.authentication.resource = ValueAdapter.toResource(
                    activity.authentication,
                    HttpAuthenticationType.RESOURCE
                );
            }

            // Only add password in the model if the field exists in the authentication type
            const hasPasswordField: boolean =
                activity.authentication.type === 'Basic' ||
                activity.authentication.type === 'ClientCertificate' ||
                (activity.authentication.type === 'ServicePrincipal' &&
                    activity.authentication.spnAuthenticationFormat ===
                        WebActivitySPNAuthenticationType.WEB_SERVICEPRINCIPAL_SECRET);

            if (activity.authentication.password && hasPasswordField) {
                modelActivity.typeProperties.authentication.password = passwordOrAKVtoSecretBase(
                    activity.authentication.password,
                    this.context
                );
            }
        }

        if (activity.raw) {
            const parsed = JSON.parse(activity.raw);
            // Overlay this on http activity
            _.merge(modelActivity, parsed);
        }

        return modelActivity;
    }

    protected async _toModel(modelActivity: Model.WebActivity): Promise<WebActivity> {
        const activity: WebActivity = WebActivity.MODEL.create<WebActivity>(
            modelActivity.name,
            modelActivity.description
        );

        this._updatedCommonProperties(modelActivity, activity);

        if (modelActivity.typeProperties) {
            if (modelActivity.typeProperties.url) {
                activity.activitySettings = WebActivitySettingsType.INLINE;
                ValueAdapter.toModel(activity, WebActivity.URL, modelActivity.typeProperties.url);
            }
            if (modelActivity.typeProperties.relativeUrl) {
                ValueAdapter.toModel(activity, WebActivity.RELATIVEURL, modelActivity.typeProperties.relativeUrl);
            }

            activity.method = modelActivity.typeProperties.method
                ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  (modelActivity.typeProperties.method.toUpperCase() as any)
                : undefined;
            activity.headers = toParameterClass(modelActivity.typeProperties.headers, activity);

            ValueAdapter.toModel(
                activity,
                WebActivity.DISABLECERTVALIDATION,
                modelActivity.typeProperties.disableCertValidation
            );
            if (this.enableRedirectDisableFeature === true) {
                ValueAdapter.toModel(
                    activity,
                    WebActivity.DISABLEFOLLOWAUTHONREDIRECT,
                    modelActivity.typeProperties.disableFollowAuthOnRedirect
                );
            }
            ValueAdapter.toModel(
                activity,
                WebActivity.HTTPREQUESTTIMEOUT,
                modelActivity.typeProperties.httpRequestTimeout
            );
            ValueAdapter.toModel(activity, WebActivity.TURNOFFASYNC, modelActivity.typeProperties.turnOffAsync);

            ValueAdapter.toModel(
                activity,
                WebActivity.GETSTATUS_URL_PATTERN,
                modelActivity.typeProperties.pollUrlPattern
            );
            ValueAdapter.toModel(activity, WebActivity.GETSTATUS_STATE_KEY, modelActivity.typeProperties.pollStatusKey);
            ValueAdapter.toModel(
                activity,
                WebActivity.GETSTATUS_SUCCESS_RESPONSE_VALUES,
                modelActivity.typeProperties.pollSuccessStatusValues
            );
            ValueAdapter.toModel(
                activity,
                WebActivity.GETSTATUS_FAILURE_RESPONSE_VALUES,
                modelActivity.typeProperties.pollFailedStatusValues
            );

            ValueAdapter.toModel(
                activity,
                WebActivity.USEFILECONTENTFORREQUESTBODY,
                modelActivity.typeProperties.useFileContentForRequestBody
            );
            ValueAdapter.toModel(
                activity,
                WebActivity.FILEPATHFORREQUESTBODY,
                modelActivity.typeProperties.filePathForRequestBody
            );
            ValueAdapter.toModel(
                activity,
                WebActivity.RESPONSEJSONPATH,
                modelActivity.typeProperties.responseJsonPath
            );

            if (modelActivity.typeProperties.linkedServiceForRequestBody) {
                activity.linkedServiceForRequestBody = ReferenceAdapter.toProxyModel<LinkedService>(
                    Metamodel.modelFor(LinkedService),
                    modelActivity.typeProperties.linkedServiceForRequestBody,
                    this.context
                );
            }

            if (modelActivity.typeProperties.connectVia) {
                activity.connectVia = ReferenceAdapter.toProxyModel<IntegrationRuntime>(
                    Metamodel.modelFor(IntegrationRuntime),
                    modelActivity.typeProperties.connectVia,
                    this.context
                );
            }

            if (modelActivity.typeProperties.linkedServices) {
                modelActivity.typeProperties.linkedServices.forEach((linkedService) => {
                    activity.linkedServices.push(
                        ReferenceAdapter.toProxyModel<LinkedService>(
                            Metamodel.modelFor(LinkedService),
                            linkedService,
                            this.context
                        )
                    );
                });
            }

            if (modelActivity.typeProperties.datasets) {
                modelActivity.typeProperties.datasets.forEach((dataset) => {
                    const refDataset = ReferenceAdapter.toProxyModel<Dataset>(
                        Metamodel.modelFor(Dataset),
                        dataset,
                        this.context
                    );
                    const paramValues: ParameterValue[] = [];
                    if (dataset.parameters) {
                        const refParam = Owned.create<ReferenceObjectParameters>(
                            ReferenceObjectParameters.MODEL,
                            activity
                        );

                        convertParametersToModel(
                            refParam,
                            !!this.context.adapterConfig.replaceIdentitiesForNonConcepts,
                            refDataset,
                            paramValues,
                            dataset.parameters
                        );

                        refParam.parameters = paramValues;
                        activity.referenceDatasetParameters.push(refParam);
                    } else {
                        activity.referenceDatasetParameters.push(
                            Owned.create<ReferenceObjectParameters>(ReferenceObjectParameters.MODEL, activity)
                        );
                    }
                    activity.datasets.push(refDataset);
                });
            }
            ValueAdapter.toModel(activity, WebActivity.BODY, modelActivity.typeProperties.body);

            if (modelActivity.typeProperties.authentication) {
                // Check if disableCertValidation is used inside client certificate authentication, move it in typeproperties
                if (
                    !activity.disableCertValidation &&
                    modelActivity.typeProperties.authentication.disableCertValidation
                ) {
                    activity.disableCertValidation = modelActivity.typeProperties.authentication.disableCertValidation;
                }

                activity.authentication = Owned.create<HttpAuthenticationType>(
                    HttpAuthenticationType.MODEL,
                    activity.authentication
                );

                if (modelActivity.typeProperties.authentication.credential) {
                    ValueAdapter.toModel(
                        activity.authentication,
                        HttpAuthenticationType.RESOURCE,
                        modelActivity.typeProperties.authentication.resource
                    );
                    activity.authentication.credential = ReferenceAdapter.toProxyModel(
                        Metamodel.modelFor(Credential),
                        modelActivity.typeProperties.authentication.credential,
                        this.context
                    );
                    activity.authentication.refType = 'Credential';
                }

                switch (modelActivity.typeProperties.authentication.type) {
                    case 'Basic':
                        activity.authentication.type = 'Basic';
                        ValueAdapter.toModel(
                            activity.authentication,
                            HttpAuthenticationType.USERNAME,
                            modelActivity.typeProperties.authentication.username
                        );
                        activity.authentication.password = isExpressionObject(
                            modelActivity.typeProperties.authentication.password
                        )
                            ? modelActivity.typeProperties.authentication.password.value
                            : secretBaseToPasswordOrAKV(
                                  modelActivity.typeProperties.authentication.password,
                                  true,
                                  this.context
                              );
                        break;
                    case 'ClientCertificate':
                        activity.authentication.type = 'ClientCertificate';
                        activity.authentication.pfx = isExpressionObject(
                            modelActivity.typeProperties.authentication.pfx
                        )
                            ? modelActivity.typeProperties.authentication.pfx.value
                            : secretBaseToPasswordOrAKV(
                                  modelActivity.typeProperties.authentication.pfx,
                                  true,
                                  this.context
                              );
                        activity.authentication.password = isExpressionObject(
                            modelActivity.typeProperties.authentication.password
                        )
                            ? modelActivity.typeProperties.authentication.password.value
                            : secretBaseToPasswordOrAKV(
                                  modelActivity.typeProperties.authentication.password,
                                  true,
                                  this.context
                              );
                        break;
                    case 'MSI':
                        activity.authentication.type = 'MSI';
                        ValueAdapter.toModel(
                            activity.authentication,
                            HttpAuthenticationType.RESOURCE,
                            modelActivity.typeProperties.authentication.resource
                        );
                        break;
                    case 'ServicePrincipal':
                        activity.authentication.type = 'ServicePrincipal';

                        // Take expressions or string value for each one
                        ValueAdapter.toModel(
                            activity.authentication,
                            HttpAuthenticationType.USERTENANT,
                            modelActivity.typeProperties.authentication.userTenant
                        );

                        // If user has provided TenantId in json outside the Spn authentication block, move it inside
                        activity.authentication.userTenant = activity.authentication.userTenant
                            ? activity.authentication.userTenant
                            : modelActivity.typeProperties.userTenant;
                        ValueAdapter.toModel(
                            activity.authentication,
                            HttpAuthenticationType.USERNAME,
                            modelActivity.typeProperties.authentication.username
                        );
                        ValueAdapter.toModel(
                            activity.authentication,
                            HttpAuthenticationType.RESOURCE,
                            modelActivity.typeProperties.authentication.resource
                        );

                        if (modelActivity.typeProperties.authentication.pfx) {
                            activity.authentication.spnAuthenticationFormat =
                                WebActivitySPNAuthenticationType.WEB_SERVICEPRINCIPAL_CERTIFICATE;
                            activity.authentication.pfx = isExpressionObject(
                                modelActivity.typeProperties.authentication.pfx
                            )
                                ? modelActivity.typeProperties.authentication.pfx.value
                                : secretBaseToPasswordOrAKV(
                                      modelActivity.typeProperties.authentication.pfx,
                                      true,
                                      this.context
                                  );
                        } else {
                            activity.authentication.spnAuthenticationFormat =
                                WebActivitySPNAuthenticationType.WEB_SERVICEPRINCIPAL_SECRET;
                            activity.authentication.password = isExpressionObject(
                                modelActivity.typeProperties.authentication.password
                            )
                                ? modelActivity.typeProperties.authentication.password.value
                                : secretBaseToPasswordOrAKV(
                                      modelActivity.typeProperties.authentication.password,
                                      true,
                                      this.context
                                  );
                        }
                        break;
                    case 'UserAuth':
                        activity.authentication.type = 'UserAuth';
                        ValueAdapter.toModel(
                            activity.authentication,
                            HttpAuthenticationType.RESOURCE,
                            modelActivity.typeProperties.authentication.resource
                        );
                        break;
                    case 'UserAssignedManagedIdentity':
                        activity.authentication.type = 'UserAssignedManagedIdentity';
                        break;
                    default:
                        break;
                }
            }
        }

        if (modelActivity.linkedServiceName !== undefined) {
            const ls = getFabricLsForParameterizedConnections(
                modelActivity.linkedServiceName,
                ModelConstants.webActivityLinkedService,
                activity);
            activity.webLinkedService = ls ? ls : ReferenceAdapter.toProxyModel<LinkedService>(
                Metamodel.modelFor(LinkedService),
                modelActivity.linkedServiceName,
                this.context
            );
            
            if (modelActivity.linkedServiceName.parameters) {
                convertParametersToModel(
                    activity,
                    !!this.context.adapterConfig.replaceIdentitiesForNonConcepts,
                    activity.webLinkedService,
                    activity.linkedServiceParameters,
                    modelActivity.linkedServiceName.parameters
                );
            }
            activity.activitySettings = __IS_FABRIC_DPE__
                ? WebActivitySettingsType.CONNECTION
                : WebActivitySettingsType.LINKED_SERVICE;
        }

        activity.raw = JSON.stringify(await ValueAdapter.remainder(this.context, modelActivity, WebActivity.MODEL));

        return activity;
    }

    private getDatasetsReferences(activity: WebActivity): Model.DatasetReference[] {
        const modelDatasetRefs: Model.DatasetReference[] = [];
        activity.datasets.forEach((dataset, index) => {
            if (dataset !== undefined) {
                const datasetReference = ReferenceAdapter.toResource(dataset, this.context) as Model.DatasetReference;
                const datasetParameters: ParameterValue[] = [];
                activity.referenceDatasetParameters[index].parameters?.forEach((param) =>
                    datasetParameters.push(param)
                );
                datasetReference.parameters = fillParameterValuesForResource(
                    datasetParameters,
                    this.context,
                    this.ensureLogger()
                );
                modelDatasetRefs.push(datasetReference);
            }
        });
        return modelDatasetRefs;
    }
}
